import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import KpiCard from "@/components/dashboard/KpiCard";
import Gauge from "@/components/dashboard/GaugeChart";
import ParetoChart from "@/components/dashboard/ParetoChart";
import SubsystemDonut from "@/components/dashboard/SubsystemDonut";
import Heatmap from "@/components/dashboard/Heatmap";
import RecentEventsTable from "@/components/dashboard/RecentEventsTable";
import QuickInsights from "@/components/dashboard/QuickInsights";
import { AlertTriangle, Clock, XCircle, TrendingUp, Wrench, CheckCircle2 } from "lucide-react";

function PanelTitle({ n, children, right }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm md:text-base tracking-tight uppercase">{children}</span>
      </div>
      {right}
    </div>
  );
}

export default function Overview() {
  const { params } = useFilters();
  const [summary, setSummary] = useState(null);
  const [pareto, setPareto] = useState({ items: [] });
  const [heat, setHeat] = useState({ days: [], bins: [], grid: [] });
  const [recent, setRecent] = useState({ items: [] });
  const [insights, setInsights] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, p, h, r, i, e] = await Promise.all([
          api.get("/metrics/summary", { params }),
          api.get("/metrics/pareto", { params }),
          api.get("/metrics/heatmap", { params }),
          api.get("/metrics/recent", { params: { ...params, limit: 5 } }),
          api.get("/metrics/insights", { params }),
          api.get("/failures", { params: { ...params, limit: 1 } }),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setPareto(p.data);
        setHeat(h.data);
        setRecent(r.data);
        setInsights(i.data.insights || []);
        setTotal(e.data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params.start, params.end, params.line, params.subsystem, tick]);

  // Realtime refresh when settings (e.g. MTBF delay filter) change.
  useEffect(() => {
    const h = () => setTick(t => t + 1);
    window.addEventListener("trams:settings-changed", h);
    return () => window.removeEventListener("trams:settings-changed", h);
  }, []);

  const c = summary?.current || {};
  const prev = summary?.previous;
  const sett = summary?.settings || {};
  const prevLabel = "prev period";

  return (
    <div className="space-y-6 pb-8" data-testid="overview-page">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
        <KpiCard testId="kpi-total-events" label="Total Failure Events" value={c.total_failure_events} prev={prev?.total_failure_events} direction="down_good" icon={AlertTriangle} iconColor="#f59e0b" iconBg="#fef3c7" prevLabel={prevLabel} />
        <KpiCard testId="kpi-total-delay" label="Total Delay (Minutes)" value={c.total_delay_minutes} prev={prev?.total_delay_minutes} direction="down_good" icon={Clock} iconColor="#f59e0b" iconBg="#fef3c7" prevLabel={prevLabel} />
        <KpiCard testId="kpi-mtbf" label="MTBF (Hours)" value={c.mtbf_hours} prev={prev?.mtbf_hours} direction="up_good" icon={TrendingUp} iconColor="#22c55e" iconBg="#dcfce7" prevLabel={prevLabel} />
        <KpiCard testId="kpi-mttr" label="MTTR (Minutes)" value={c.mttr_minutes} prev={prev?.mttr_minutes} direction="down_good" icon={Wrench} iconColor="#3b82f6" iconBg="#dbeafe" prevLabel={prevLabel} />
        <KpiCard testId="kpi-availability" label="Availability (%)" value={c.availability_percent} unit="%" prev={prev?.availability_percent} direction="up_good" icon={CheckCircle2} iconColor="#22c55e" iconBg="#dcfce7" prevLabel={prevLabel} />
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow" data-testid="panel-overview-gauges">
          <PanelTitle n="1">RAMS Operational Overview</PanelTitle>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mt-4">
            <Gauge
              testId="gauge-reliability"
              label={<><span>Reliability</span><br/><span className="text-slate-400">(MTBF)</span></>}
              value={c.mtbf_hours}
              max={Math.max(sett.target_mtbf_hours * 1.5 || 200, c.mtbf_hours || 0)}
              target={sett.target_mtbf_hours || 100}
              unit="Hours"
              direction="up_good"
            />
            <Gauge
              testId="gauge-availability"
              label={<><span>Availability</span><br/><span className="text-slate-400">(%)</span></>}
              value={c.availability_percent}
              max={100}
              target={sett.target_availability || 98.5}
              unit="Percent"
              direction="up_good"
            />
            <Gauge
              testId="gauge-maintainability"
              label={<><span>Maintainability</span><br/><span className="text-slate-400">(MTTR)</span></>}
              value={c.mttr_minutes}
              max={Math.max((sett.target_mttr_minutes || 25) * 2, c.mttr_minutes || 0)}
              target={sett.target_mttr_minutes || 25}
              unit="Minutes"
              direction="down_good"
            />
            <Gauge
              testId="gauge-safety"
              label={<><span>Safety</span><br/><span className="text-slate-400">(Severity)</span></>}
              value={c.safety_score}
              max={Math.max((sett.target_safety || 0.10) * 5, c.safety_score || 0)}
              target={sett.target_safety || 0.10}
              unit="Score"
              direction="down_good"
            />
          </div>
        </div>

        <div className="lg:col-span-5 bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow" data-testid="panel-heatmap">
          <PanelTitle n="2">Failure Events Heatmap</PanelTitle>
          <Heatmap {...heat} />
        </div>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow" data-testid="panel-pareto">
          <PanelTitle n="3">Pareto – Failure by Subsystem</PanelTitle>
          <ParetoChart items={pareto.items} />
        </div>
        <div className="lg:col-span-5 bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow" data-testid="panel-modes">
          <PanelTitle n="4">Top Failure</PanelTitle>
          <SubsystemDonut items={pareto.items} />
        </div>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow" data-testid="panel-recent">
          <PanelTitle n="5">Recent Failure Events</PanelTitle>
          <RecentEventsTable items={recent.items} total={total} />
        </div>
        <div className="lg:col-span-4 bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow" data-testid="panel-insights">
          <PanelTitle n="6">Quick Insights</PanelTitle>
          <QuickInsights insights={insights} />
        </div>
      </div>

      {loading && (
        <div className="fixed top-24 right-6 bg-white shadow-lg rounded-lg px-3 py-1.5 text-xs text-slate-600 border border-slate-200 z-40">
          Refreshing…
        </div>
      )}
    </div>
  );
}
